<template>
  <!-- Modal backdrop -->
  <div
    v-if="visible"
    class="modal-backdrop fade show"
    @click="handleCancel"
  ></div>

  <!-- Modal dialog -->
  <div
    v-if="visible"
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    @keydown.esc="handleCancel"
  >
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content" @click.stop>
        <!-- Modal header -->
        <div class="modal-header">
          <h5 class="modal-title">{{ title }}</h5>
          <button
            type="button"
            class="btn-close"
            aria-label="Close"
            @click="handleCancel"
          ></button>
        </div>

        <!-- Modal body -->
        <div class="modal-body">
          <p v-if="message" class="mb-3">{{ message }}</p>

          <!-- Dropdown mode -->
          <div v-if="inputType === 'select'" class="mb-3">
            <label v-if="inputLabel" class="form-label">{{ inputLabel }}</label>
            <select
              ref="inputField"
              v-model="inputValue"
              class="form-select"
              @keydown.enter="handleConfirm"
            >
              <option
                v-for="opt in options"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>

          <!-- Text input mode -->
          <div v-else-if="inputType === 'text'" class="mb-3">
            <label v-if="inputLabel" class="form-label">{{ inputLabel }}</label>
            <input
              ref="inputField"
              v-model="inputValue"
              type="text"
              class="form-control"
              :placeholder="placeholder"
              @keydown.enter="handleConfirm"
            />
          </div>

          <!-- Textarea mode -->
          <div v-else-if="inputType === 'textarea'" class="mb-3">
            <label v-if="inputLabel" class="form-label">{{ inputLabel }}</label>
            <textarea
              ref="inputField"
              v-model="inputValue"
              class="form-control"
              :placeholder="placeholder"
              rows="3"
            ></textarea>
          </div>

          <!-- Validation error -->
          <div v-if="validationError" class="text-danger small mt-2">
            {{ validationError }}
          </div>
        </div>

        <!-- Modal footer -->
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="handleCancel"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="handleConfirm"
          >
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "InputModal",
  props: {
    // Controls modal visibility
    visible: {
      type: Boolean,
      default: false,
    },
    // Modal title
    title: {
      type: String,
      default: "Input Required",
    },
    // Optional message/description
    message: {
      type: String,
      default: "",
    },
    // Input type: 'text', 'textarea', or 'select'
    inputType: {
      type: String,
      default: "text",
      validator: (v) => ["text", "textarea", "select"].includes(v),
    },
    // Label for the input field
    inputLabel: {
      type: String,
      default: "",
    },
    // Placeholder text for text/textarea inputs
    placeholder: {
      type: String,
      default: "",
    },
    // Initial value for the input
    initialValue: {
      type: String,
      default: "",
    },
    // Options for select input: [{ value: 'x', label: 'X' }, ...]
    options: {
      type: Array,
      default: () => [],
    },
    // Text for confirm button
    confirmText: {
      type: String,
      default: "Confirm",
    },
    // Whether input is required
    required: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["confirm", "cancel"],
  data() {
    return {
      inputValue: "",
      validationError: "",
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // Reset state when modal opens
        this.inputValue = this.initialValue;
        this.validationError = "";
        // Focus input on next tick
        this.$nextTick(() => {
          if (this.$refs.inputField) {
            this.$refs.inputField.focus();
          }
        });
      }
    },
    initialValue(newVal) {
      this.inputValue = newVal;
    },
  },
  mounted() {
    // Initialize input value
    this.inputValue = this.initialValue;
  },
  methods: {
    handleConfirm() {
      // Validate if required
      if (this.required && !this.inputValue.trim()) {
        this.validationError = "This field is required.";
        return;
      }
      this.validationError = "";
      this.$emit("confirm", this.inputValue);
    },
    handleCancel() {
      this.validationError = "";
      this.$emit("cancel");
    },
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

.modal {
  z-index: 1050;
}
</style>
